import { useState } from "react";
import { Maximize2 } from "lucide-react";
import { resolveImageUrl } from "../lib/api";
import { Modal } from "./ui/Modal";

export interface QuestionFigureProps {
  figureImage?: string | null;
  image?: string | null;
  figureAlt?: string | null;
  alt?: string | null;
  figureCaption?: string | null;
  location?: unknown;
  className?: string;
}

export function QuestionFigure({
  figureImage,
  image,
  figureAlt,
  alt,
  figureCaption,
  className = "",
}: QuestionFigureProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const resolvedUrl = resolveImageUrl(figureImage || image);
  if (!resolvedUrl) return null;

  const altText = (figureAlt || alt || figureCaption || "Question figure").trim();

  return (
    <>
      <figure className={`my-3 overflow-hidden rounded-md border border-border bg-muted/20 ${className}`}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => setIsModalOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsModalOpen(true);
            }
          }}
          className="group relative block max-w-full cursor-zoom-in overflow-hidden focus:outline-none focus:ring-2 focus:ring-primary/50"
          title="Click to enlarge"
        >
          <img
            src={resolvedUrl}
            alt={altText}
            loading="lazy"
            className="max-h-80 w-auto max-w-full rounded-t-md object-contain transition duration-200 group-hover:scale-[1.01]"
          />
          <div className="absolute top-2 right-2 flex items-center gap-1 rounded bg-black/60 px-2 py-1 text-xs font-medium text-white opacity-0 backdrop-blur-xs transition group-hover:opacity-100">
            <Maximize2 size={12} />
            <span>Enlarge</span>
          </div>
        </div>
        {(figureCaption || figureAlt) && (
          <figcaption className="border-t border-border/60 bg-card/60 px-3 py-1.5 text-xs text-muted-foreground">
            {figureCaption || figureAlt}
          </figcaption>
        )}
      </figure>

      {/* Lightbox Tap to Enlarge Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        size="xl"
        title={altText || "Figure view"}
      >
        <div className="flex flex-col items-center">
          <div className="max-h-[75vh] overflow-auto w-full flex justify-center">
            <img
              src={resolvedUrl}
              alt={altText}
              className="mx-auto h-auto max-h-[70vh] w-auto max-w-full object-contain rounded-md"
            />
          </div>
          {altText && (
            <p className="mt-3 text-center text-xs text-muted-foreground px-2">
              {altText}
            </p>
          )}
        </div>
      </Modal>
    </>
  );
}
